// -------------------------------------------------------------------------------
// Libraries
// -------------------------------------------------------------------------------
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// -------------------------------------------------------------------------------
// Store
// -------------------------------------------------------------------------------
import { useRecoilValue } from "recoil";
import { userAtom } from "globalAtoms";
import {
	useStripeCreateOnboardingLinkMutation,
	useStripeGetCustomerConnectedExternalAccountsMutation,
	useStripeGetPaymentMethodsMutation,
	useGetDomainGymQuery,
	useStripeGetGymSubscriptionQuery,
} from "store/api/api";

// -------------------------------------------------------------------------------
// Components
// -------------------------------------------------------------------------------
import { Paragraph9, Paragraph2 } from "themes/default/_typography";
import SetupPaymentMethodModal from "components/Modals/SetupPaymentMethodModal";
import { LoadingMask } from "components/Common/LoadingMask";
import { Button } from "components/Common/Button";
import StripeAlerts from "components/Alerts";
import { DividerHeader } from "components/Common/DividerHeader";
import { PaymentIntentCard } from "components/Common/Cards/PaymentIntentCard";
import { BankAccountCard } from "components/Common/Cards/BankAccountCard";
import { PaymentMethodCard } from "components/Common/Cards/PaymentMethodCard";
import { MembershipSettings } from "../MembershipSettings";

// -------------------------------------------------------------------------------
// Utils, Styles and Assets
// -------------------------------------------------------------------------------
import StyledPricingPlanPage from "./styles";
import { paymentLevels } from "helpers/constants";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";

// -------------------------------------------------------------------------------
// Component
// -------------------------------------------------------------------------------

function PricingPage({
	subscription,
	planDetails,
	fetchCustomerPaymentMethods = () => {},
	isDomainGymDataLoading = false,
	openStripeAccountLink = (type) => openStripeAccountLink(type),
	getGymSubscriptionResponse = { isLoading: false, data: null },
	domainGymData,
	setShowPaymentMethodModal = () => {},
	showPaymentMethodModal = false,
	navigate = () => {},
	gym = "",
	paymentMethods,
}) {
	if (getGymSubscriptionResponse.isLoading || isDomainGymDataLoading) {
		return <LoadingMask />;
	}
	return (
		<div style={{ padding: 10 }} className="sb-pricing-plan-page__right">
			<div className="sb-pricing-plan-page__right__test">
				<DividerHeader text="Pricing Plan" uppercase />
				{subscription && planDetails && (
					<div key={subscription.id}>
						<div className="sb-payment-settings__memberships">
							<PaymentIntentCard
								icon={planDetails.icon}
								plan={planDetails.title}
								freeSubs={planDetails.freeSubs}
								pricing={subscription.plan.amount / 100}
								planDetails={planDetails}
								refetch={fetchCustomerPaymentMethods}
								subscription={subscription}
							/>
						</div>
					</div>
				)}
				{!subscription && (
					<div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
						<Button
							className="primary-filled--branding-primary-color"
							uppercase
							onClick={() => navigate(`/${gym}/dashboard/select-plan`)}
						>
							<Paragraph9>Choose A Plan</Paragraph9>
						</Button>
					</div>
				)}

				<div className="sb-pricing-plan-page__right__notification">
					<div style={{ paddingRight: "16px" }}>
						<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M19.61 11.16C19.3198 10.4139 19.3198 9.58613 19.61 8.84002L19.78 8.38002C20.0783 7.6007 20.0674 6.73683 19.7495 5.9653C19.4316 5.19377 18.8307 4.57297 18.07 4.23002L17.62 4.00002C16.9044 3.67008 16.33 3.09564 16 2.38002L15.8 1.93002C15.459 1.16765 14.8383 0.565249 14.0662 0.247071C13.294 -0.0711071 12.4291 -0.0807898 11.65 0.22002L11.19 0.39002C10.8096 0.532668 10.4062 0.603861 10 0.60002C9.60387 0.600211 9.21093 0.529077 8.84001 0.39002L8.38001 0.22002C7.60089 -0.0807898 6.73604 -0.0711071 5.96385 0.247071C5.19166 0.565249 4.57104 1.16765 4.23001 1.93002L4.00001 2.38002C3.67006 3.09564 3.09563 3.67008 2.38001 4.00002L1.93001 4.20002C1.16928 4.54297 0.568448 5.16377 0.250541 5.9353C-0.0673649 6.70683 -0.0782889 7.5707 0.220006 8.35002L0.390006 8.81002C0.680209 9.55613 0.680209 10.3839 0.390006 11.13L0.220006 11.59C-0.0782889 12.3693 -0.0673649 13.2332 0.250541 14.0047C0.568448 14.7763 1.16928 15.3971 1.93001 15.74L2.38001 15.94C3.10607 16.2848 3.68189 16.8819 4.00001 17.62L4.20001 18.07C4.54104 18.8324 5.16166 19.4348 5.93385 19.753C6.70604 20.0711 7.57089 20.0808 8.35001 19.78L8.81001 19.61C9.55801 19.3301 10.382 19.3301 11.13 19.61L11.59 19.78C12.3691 20.0808 13.234 20.0711 14.0062 19.753C14.7783 19.4348 15.399 18.8324 15.74 18.07L15.94 17.62C16.2847 16.894 16.8819 16.3181 17.62 16L18.07 15.8C18.8307 15.4571 19.4316 14.8363 19.7495 14.0647C20.0674 13.2932 20.0783 12.4293 19.78 11.65L19.61 11.16ZM17.26 13.94L16.8 14.15C15.6179 14.6816 14.6716 15.6279 14.14 16.81L13.95 17.25C13.8496 17.4736 13.6867 17.6634 13.4809 17.7965C13.2751 17.9296 13.0351 18.0003 12.79 18C12.6261 17.9974 12.4638 17.9669 12.31 17.91L11.88 17.75C11.2813 17.5149 10.6432 17.3961 10 17.4C9.36738 17.3953 8.73961 17.5107 8.15001 17.74L7.66001 17.91C7.51742 17.9694 7.36448 18 7.21001 18C6.96715 18.0017 6.72907 17.9326 6.52484 17.8012C6.32061 17.6698 6.15909 17.4817 6.06001 17.26L5.85001 16.8C5.31842 15.6179 4.3721 14.6716 3.19001 14.14L2.75001 14C2.45186 13.8621 2.21756 13.6156 2.09492 13.3108C1.97227 13.0061 1.97052 12.666 2.09001 12.36L2.25001 11.93C2.71511 10.7302 2.71511 9.39986 2.25001 8.20002L2.08001 7.71002C1.96168 7.41092 1.96321 7.07772 2.08427 6.77972C2.20534 6.48172 2.43661 6.24186 2.73001 6.11002L3.19001 5.90002C4.38472 5.35824 5.33605 4.39265 5.86001 3.19002L6.05001 2.75002C6.15036 2.5264 6.31329 2.3366 6.51912 2.20352C6.72495 2.07044 6.9649 1.99976 7.21001 2.00002C7.37394 2.00267 7.53624 2.0331 7.69001 2.09002L8.12001 2.25002C8.71873 2.48512 9.35679 2.60391 10 2.60002C10.6326 2.60475 11.2604 2.48937 11.85 2.26002L12.34 2.09002C12.4826 2.03061 12.6355 2.00002 12.79 2.00002C13.0329 1.99832 13.2709 2.0674 13.4752 2.19881C13.6794 2.33023 13.8409 2.51829 13.94 2.74002L14.15 3.20002C14.6816 4.38211 15.6279 5.32843 16.81 5.86002L17.25 6.05002C17.5482 6.18795 17.7824 6.43444 17.9051 6.73919C18.0277 7.04395 18.0295 7.38401 17.91 7.69002L17.75 8.12002C17.2849 9.31986 17.2849 10.6502 17.75 11.85L17.92 12.34C18.0377 12.6403 18.0347 12.9745 17.9117 13.2727C17.7887 13.5709 17.5552 13.81 17.26 13.94ZM10.5 12H9.50001C9.3674 12 9.24022 12.0527 9.14645 12.1465C9.05268 12.2402 9.00001 12.3674 9.00001 12.5V13.5C9.00001 13.6326 9.05268 13.7598 9.14645 13.8536C9.24022 13.9473 9.3674 14 9.50001 14H10.5C10.6326 14 10.7598 13.9473 10.8536 13.8536C10.9473 13.7598 11 13.6326 11 13.5V12.5C11 12.3674 10.9473 12.2402 10.8536 12.1465C10.7598 12.0527 10.6326 12 10.5 12ZM10.68 6.00002H9.32001C9.24898 5.99951 9.17865 6.01413 9.11372 6.04292C9.04879 6.07172 8.99073 6.11401 8.94342 6.167C8.89612 6.21998 8.86064 6.28244 8.83936 6.35021C8.81808 6.41798 8.81148 6.4895 8.82001 6.56002L9.22001 9.78002C9.22738 9.841 9.25693 9.89713 9.30304 9.93771C9.34915 9.97828 9.40859 10.0005 9.47001 10H10.53C10.5914 10.0005 10.6509 9.97828 10.697 9.93771C10.7431 9.89713 10.7726 9.841 10.78 9.78002L11.18 6.56002C11.1885 6.4895 11.1819 6.41798 11.1607 6.35021C11.1394 6.28244 11.1039 6.21998 11.0566 6.167C11.0093 6.11401 10.9512 6.07172 10.8863 6.04292C10.8214 6.01413 10.751 5.99951 10.68 6.00002Z"
								fill="#4F5661"
							/>
						</svg>
					</div>
					<p className="sb-pricing-plan-page__right__notification__text">
						Going over your subscriber limit during a month you will be charged $3/user for that month. Overage charges
						will appear on the invoice of the month after. The following month, you will be automatically moved up to
						the next tier and must downgrade yourself in your account settings if you do not wish to be charged at a
						higher tier.
					</p>
				</div>
				<DividerHeader text="Payment Methods" uppercase />
				<Button
					style={{ width: "100%", maxWidth: "unset", marginBottom: 16 }}
					onClick={() => setShowPaymentMethodModal(true)}
					className="primary-outlined"
				>
					<Paragraph2>Add a Payment Method</Paragraph2>
				</Button>
				{paymentMethods?.map((pm, i) => {
					return <PaymentMethodCard key={pm.id} paymentMethod={pm} refetch={fetchCustomerPaymentMethods} />;
				})}
			</div>
			<SetupPaymentMethodModal
				open={showPaymentMethodModal}
				onClose={() => setShowPaymentMethodModal(false)}
				type="gym"
				customerId={domainGymData?.customer_id}
				afterComplete={async () => {
					await fetchCustomerPaymentMethods?.();
					setShowPaymentMethodModal(false);
				}}
			/>
		</div>
	);
}

function MembershipPage({ subscription }) {
	return (
		<div className="sb-pricing-plan-page__right">
			<DividerHeader text="Member Plan" uppercase />
			<MembershipSettings subscription={subscription} />
		</div>
	);
}

function EarningsPage({ bankAccount, openStripeAccountLink = (type) => openStripeAccountLink(type) }) {
	return (
		<div style={{ width: "100%" }}>
			<StripeAlerts />
			<div className="sb-pricing-plan-page__right">
				<DividerHeader text="Earnings Withdrawal Account" uppercase />

				<div className="sb-payment-settings__payment-section" style={{ width: "100%" }}>
					{bankAccount && (
						<BankAccountCard bankAccount={bankAccount} onEdit={() => openStripeAccountLink("account_update")} />
					)}
					{!bankAccount && (
						<Button
							className="primary-outlined"
							uppercase
							plusIcon
							onClick={() => openStripeAccountLink("account_onboarding")}
						>
							Add Payment Account
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}
const PricingPlanPage = () => {
	const { gym } = useParams();
	const navigate = useNavigate();
	const user = useRecoilValue(userAtom);
	const primaryColor = usePrimaryColor();
	const [getPaymentMethodsTrigger, getPaymentMethodsResponse] = useStripeGetPaymentMethodsMutation();
	const [createOnboardingLinkTrigger] = useStripeCreateOnboardingLinkMutation();
	const [getConnectedAccountsTrigger] = useStripeGetCustomerConnectedExternalAccountsMutation();
	const { data: domainGymData, isLoading: isDomainGymDataLoading } = useGetDomainGymQuery(gym);
	const getGymSubscriptionResponse = useStripeGetGymSubscriptionQuery(
		{ gymId: domainGymData?.id },
		{ skip: !domainGymData?.id }
	);
	const [searchParams, setSearchParams] = useSearchParams();

	const [currentPage, setCurrentPage] = useState(searchParams.get("currentPage") || "sweatbase");
	const [connectedAccountId, setConnectedAccountId] = useState(null);
	const [bankAccount, setBankAccount] = useState(null);
	const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);

	const isUsedSBPayments = useMemo(() => !!(domainGymData?.is_used_sb_payments ?? 1), [domainGymData]);

	const paymentMethods = getPaymentMethodsResponse.data || [];
	const subscription = getGymSubscriptionResponse?.data?.subscription;
	const planDetails = paymentLevels[subscription?.plan?.product?.name];

	useEffect(() => {
		if (user?.gym?.stripe_info?.stripe_connected_account_id) {
			setConnectedAccountId(user.gym.stripe_info.stripe_connected_account_id);
		}
	}, [user]);

	useEffect(() => {
		if (!isUsedSBPayments) {
			navigate("/");
		}
	}, [isUsedSBPayments, navigate]);

	useEffect(() => {
		if (connectedAccountId) {
			fetchCustomerPaymentMethods();
			fetchCustomerConnectedExternalAccounts();
		}
		if (getGymSubscriptionResponse?.data && domainGymData?.id) {
			getGymSubscriptionResponse?.refetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connectedAccountId]);

	const openStripeAccountLink = async (type) => {
		try {
			const getConnectedAcct = await createOnboardingLinkTrigger({
				return_url: window.location.href,
				account_id: connectedAccountId,
				type,
			}).unwrap();

			window.location.href = getConnectedAcct.url;
		} catch (error) {}
	};

	const fetchCustomerConnectedExternalAccounts = async () => {
		try {
			const bankAccountsRes = await getConnectedAccountsTrigger({
				account_id: connectedAccountId,
			}).unwrap();

			if (bankAccountsRes?.data?.length > 0) {
				const data = bankAccountsRes.data[0];
				setBankAccount(data);
			}
		} catch (error) {
			console.error("ERROR: ", error);
		}
	};

	const fetchCustomerPaymentMethods = async () => {
		if (domainGymData?.customer_id) {
			try {
				await getPaymentMethodsTrigger({ customer_id: domainGymData?.customer_id });
			} catch (error) {
				console.log("Error fetching payment methods: ", error?.message);
			}
		}
	};

	const navigateTo = (currentPage) => {
		setCurrentPage(currentPage);
		setSearchParams({ currentPage });
	};

	return (
		<StyledPricingPlanPage primaryColor={primaryColor} className="sb-pricing-plan-page">
			{isDomainGymDataLoading ? (
				<LoadingMask />
			) : (
				<div className="sb-pricing-plan-page">
					<div className="sb-pricing-plan-page__left">
						<p className="sb-pricing-plan-page__left__text">Pricing Settings</p>
						<div className="sb-pricing-plan-page__left__divider"></div>

						<div
							onClick={() => navigateTo("sweatbase")}
							className={
								currentPage === "sweatbase"
									? "sb-pricing-plan-page__left__tab-text-container__selected"
									: "sb-pricing-plan-page__left__tab-text-container"
							}
						>
							<p className="sb-pricing-plan-page__left__tab-text">Sweatbase plan</p>
						</div>

						<div
							onClick={() => navigateTo("member")}
							className={
								currentPage === "member"
									? "sb-pricing-plan-page__left__tab-text-container__selected"
									: "sb-pricing-plan-page__left__tab-text-container"
							}
						>
							<p className="sb-pricing-plan-page__left__tab-text">Member plan</p>
						</div>

						<div
							onClick={() => navigateTo("earnings")}
							className={
								currentPage === "earnings"
									? "sb-pricing-plan-page__left__tab-text-container__selected"
									: "sb-pricing-plan-page__left__tab-text-container"
							}
						>
							<p className="sb-pricing-plan-page__left__tab-text">Earnings account</p>
						</div>
					</div>
					{currentPage === "sweatbase" && (
						<PricingPage
							subscription={subscription}
							planDetails={planDetails}
							fetchCustomerPaymentMethods={fetchCustomerPaymentMethods}
							isDomainGymDataLoading={isDomainGymDataLoading}
							openStripeAccountLink={openStripeAccountLink}
							getGymSubscriptionResponse={getGymSubscriptionResponse}
							domainGymData={domainGymData}
							setShowPaymentMethodModal={setShowPaymentMethodModal}
							showPaymentMethodModal={showPaymentMethodModal}
							navigate={navigate}
							gym={gym}
							paymentMethods={paymentMethods}
						/>
					)}
					{currentPage === "member" && <MembershipPage subscription={subscription} />}
					{currentPage === "earnings" && (
						<EarningsPage openStripeAccountLink={openStripeAccountLink} bankAccount={bankAccount} />
					)}
				</div>
			)}
		</StyledPricingPlanPage>
	);
};

export default PricingPlanPage;

